import { observable, action, makeObservable } from 'mobx';
import { Ilang } from 'types';
import { toast, toastTypeEnum } from 'common/utils';
import { getApiErrorMessage } from 'services/utils';

import {
  getAllKeyWords,
  addNewKeyWord,
  updateKeyWord,
  deleteKeyWord
} from 'services/languages';

export type IInitialState = {
  data: {
    keywords: ILang[];
    selectedRecord: Ilang | null;
    limit: number;
    search: string;
    offset: number;
    total: number;
  };
  ui: {
    getKeyWordsLoading: boolean;
  };
};

export const initialState: IInitialState = {
  data: {
    keywords: [],
    selectedRecord: null,
    search: '',
    limit: 10,
    offset: 1,
    total: 0
  },
  ui: {
    getKeyWordsLoading: false
  }
};

export class LanguageStore {
  state: IInitialState = initialState;

  getLanguagesKeyWord = async (search = '', limit = 10, offset = 1) => {
    try {
      this.setState({ ui: { ...this.state.ui, getKeyWordsLoading: true } });
      const { data } = await getAllKeyWords({
        ...{ search: search, limit },
        offset: (offset - 1) * limit
      });
      this.setState({
        data: {
          ...this.state.data,
          keywords: data.data.rows,
          total: data.data.count,
          limit: limit,
          offset: offset
        }
      });
    } catch (error) {
      const message = getApiErrorMessage(error);
      toast(toastTypeEnum.error, message);
    } finally {
      this.setState({ ui: { ...this.state.ui, getKeyWordsLoading: false } });
    }
  };

  addNewLangaugeKeyword = async (values: Ilang) => {
    try {
      this.setState({ ui: { ...this.state.ui, getKeyWordsLoading: true } });
      await addNewKeyWord(values);
    } catch (error) {
      const message = getApiErrorMessage(error);
      toast(toastTypeEnum.error, message);
    } finally {
      this.setState({ ui: { ...this.state.ui, getKeyWordsLoading: false } });
    }
  };

  updateLanguageKeyword = async (values: Ilang) => {
    try {
      this.setState({ ui: { ...this.state.ui, getKeyWordsLoading: true } });
      await updateKeyWord(values);
    } catch (error) {
      const message = getApiErrorMessage(error);
      toast(toastTypeEnum.error, message);
    } finally {
      this.setState({ ui: { ...this.state.ui, getKeyWordsLoading: false } });
    }
  };

  deleteLangaugeKeyWord = async (values: Ilang) => {
    try {
      this.setState({ ui: { ...this.state.ui, getKeyWordsLoading: true } });
      await deleteKeyWord(values.id);
    } catch (error) {
      const message = getApiErrorMessage(error);
      toast(toastTypeEnum.error, message);
    } finally {
      this.setState({ ui: { ...this.state.ui, getKeyWordsLoading: false } });
    }
  };

  setState = (params: Partial<IInitialState>) => {
    const { state } = this;
    this.state = {
      ...state,
      ...params
    };
    /* use this space to save/sync state with localStorage */
  };

  reset = () => {
    this.state = initialState;
    /* use this space to reset localStorage in case if you're persisting this state */
  };

  constructor() {
    makeObservable(this, {
      state: observable,
      getLanguagesKeyWord: action,
      addNewLangaugeKeyword: action,
      updateLanguageKeyword: action,
      deleteLangaugeKeyWord: action,
      setState: action,
      reset: action
    });
  }
}

export type ILang = {
  id: string;
  default: string;
  ar?: string;
  ja?: string;
  ru?: string;
  zh?: string;
  createdAt: string;
  updatedAt: string;
};
